import React, { FC } from 'react';
import { ReferenceField, useRefresh, useTranslate } from 'react-admin';
import Grid from '@material-ui/core/Grid';
import makeStyles from '@material-ui/core/styles/makeStyles';

import { AccountsActions, AccountWithErrorWarning, H2, TextField } from '~/components';
import { Section } from '~/layout';

import { TextField as TextFieldRa } from '~/components/ra';
import { Longdash, time } from '~/utils';
import EditSourceName from '~/components/EditSourceName';
import BalancesTable from '~/components/ra/Accounts/BalancesTable';
import LimitsAndCounters from '~/components/ra/Accounts/LimitsAndCounters';
import { useRightContentSpacing } from '~/hooks/styles';
import { Theme } from '@material-ui/core';
import { Account } from '~/types';
import { useProductTemplates } from '~/hooks/useProductTemplates';
import { getResourceByProfileType } from '~/utils/profileType';
import Alert from '@material-ui/lab/Alert';

interface DetailsTabProps {
  record: Account;
  history?: any;
}

const DetailsTab: FC<DetailsTabProps> = (props) => {
  const { record } = props;
  const translate = useTranslate();
  const refetch = useRefresh();
  const t = (key: string) => translate(`resources.accounts.show.details.${key}`);

  const spacing = useRightContentSpacing();
  const classes = useStyles({ spacing });

  const profileResource = getResourceByProfileType(record.profileType);

  const { data: productTemplates, isLoading } = useProductTemplates();
  const productTemplateAlias = productTemplates?.find(
    (item) => item.code === record?.productTemplate?.code
  );

  const isFallback = record?.productTemplate.code === 'FALLBACK';

  return (
    <>
      {record?.error && (
        <Grid container className={classes.bottomSpacing}>
          <Grid item xs={12}>
            <AccountWithErrorWarning
              accountId={record?.id}
              error={record?.error}
              refetch={refetch}
            />
          </Grid>
        </Grid>
      )}
      <Grid container justify='space-between' className={classes.bottomSpacing}>
        <Grid item xs={12} className={classes.headerSection}>
          <Grid container justify='space-between' alignItems='flex-start'>
            {isFallback && (
              <Grid item xs={12}>
                <Alert severity='warning' style={{ marginBottom: '1em', marginTop: '0.5em' }}>
                  {t('productTemplateFallback')}
                </Alert>
              </Grid>
            )}
            <Grid item>
              <H2 topSpacing={0} bottomSpacing={2}>
                {t('systemInfo')}
              </H2>
            </Grid>
            <Grid item className='sectionActions'>
              {profileResource !== 'partners' && profileResource !== 'tenant/accounts' && (
                <AccountsActions
                  status={record.status}
                  resource='accounts'
                  record={record}
                  refetch={refetch as any}
                  // profileResource={profileResource} not needed
                />
              )}
            </Grid>
          </Grid>
          <Section>
            <Grid container spacing={2}>
              {!isFallback && (
                <Grid item xs={4}>
                  <TextField loading={isLoading} label={t('productTemplateName')}>
                    {productTemplateAlias?.alias}
                  </TextField>
                </Grid>
              )}
              {!isFallback && (
                <Grid item xs={4}>
                  <TextField label={t('productTemplateCode')}>
                    {record.productTemplate.code}
                  </TextField>
                </Grid>
              )}
              {!isFallback && (
                <Grid item xs={4}>
                  <TextField label={t('productTemplateCategory')}>
                    {record.productTemplate.category}
                  </TextField>
                </Grid>
              )}
              <Grid item xs={4}>
                {record.bankAccountId ? (
                  <ReferenceField
                    source='bankAccountId'
                    reference='bank/accounts'
                    link={false}
                    addLabel={false}
                    {...props}
                  >
                    <TextFieldRa
                      source='alias'
                      label={t('liquidityBucket')}
                      showLink={`/bank/accounts/${record.bankAccountId}/show`}
                    />
                  </ReferenceField>
                ) : (
                  <TextField label={t('liquidityBucket')} />
                )}
              </Grid>
              <Grid item xs={4}>
                <TextField label={t('currency')}>{record.currency}</TextField>
              </Grid>
              <Grid item xs={4}>
                <TextField label={t('currentDay')}>{time(record.currentDay).format('l')}</TextField>
              </Grid>
              {record?.closed && (
                <Grid item xs={12}>
                  <TextField label={t('closedOn')} copyable>
                    {time(record?.closed).format('L')}
                  </TextField>
                </Grid>
              )}
            </Grid>
          </Section>
        </Grid>
      </Grid>

      <Grid container justify='space-between' className={classes.bottomSpacing}>
        <Grid item xs={12} className={classes.headerSection}>
          <Grid container justify='space-between' alignItems='flex-start'>
            <Grid item>
              <H2 topSpacing={0} bottomSpacing={2}>
                {t('aliasAndDescription')}
              </H2>
            </Grid>
            <Grid item className='sectionActions'>
              <EditSourceName source='accounts' resource={profileResource} record={record} />
            </Grid>
          </Grid>
          <Section>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField label={t('alias')}>{record.alias}</TextField>
              </Grid>
              <Grid item xs={8}>
                <TextField multiline label={t('description')}>
                  {record.description || Longdash}
                </TextField>
              </Grid>
            </Grid>
          </Section>
        </Grid>
      </Grid>
      {!isFallback && (
        <Grid container justify='space-between' className={classes.bottomSpacing}>
          <Grid item xs={12}>
            <H2 topSpacing={0} bottomSpacing={0}>
              {t('balancesAndCounters')}
            </H2>
            <Section>
              <Grid container spacing={2} className={classes.container}>
                <Grid item xs={12}>
                  {record.balances && record.balances.length > 0 && (
                    <BalancesTable balances={record.balances} resource={profileResource} />
                  )}
                </Grid>
                <Grid item xs={12}>
                  {record.id && <LimitsAndCounters id={record.id} />}
                </Grid>
              </Grid>
            </Section>
          </Grid>
        </Grid>
      )}
    </>
  );
};

type StylesProps = {
  spacing: number;
};

const useStyles = makeStyles<Theme, StylesProps>((theme) => ({
  container: {
    paddingLeft: theme.spacing(4),
  },
  rightContent: {
    marginLeft: ({ spacing }) => theme.spacing(spacing),
  },
  marginLeftAside: {
    marginLeft: '24px',
  },
  bottomSpacing: {
    paddingBottom: theme.spacing(4),
  },
  headerSection: {
    '& .sectionActions': {
      opacity: 0.5,
      transition: `opacity ${theme.transitions.easing.easeIn}`,
      transitionDuration: `${theme.transitions.duration.shortest}ms`,
    },
    '&:hover': {
      '& .sectionActions': {
        opacity: 1,
      },
    },
  },
  error: {
    color: theme.palette.error.main,
  },
}));

export default DetailsTab;
